import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CheckCircle } from 'lucide-react'
import Seo from '../components/Seo'
import { coachingPricing } from '../lib/coachingPrograms'
import { EnrollLink, tierKey } from '../components/ui/EnrollLink'

const CoachingPricing: React.FC = () => {
  const strongAgainIncludes = [
    'Fully customized strength-training program',
    'Personalized nutrition coaching',
    'Cardio programming when appropriate',
    'Supplement recommendations when appropriate',
    'Regular coaching consultations',
    'Progress reviews and program adjustments',
    'Recovery and stress-management guidance',
    'Direct coaching support from Cassie',
  ]

  const nutritionIncludes = [
    'Personalized nutrition strategy',
    'Calorie and macro guidance',
    'Protein-first nutrition framework',
    'Meal structure built around your real life',
    'Practical strategies for consistency',
  ]

  const competitionIncludes = [
    'Customized training and nutrition programming',
    'Cardio programming when appropriate',
    'Supplementation recommendations',
    'Physique assessments and progress reviews',
    'Coaching consultations and adjustments',
  ]

  const proLookIncludes = [
    'Customized training and nutrition strategy',
    'Physique-focused programming',
    'Cardio programming when appropriate',
    'Supplement recommendations when appropriate',
    'Physique assessments and progress reviews',
    'Regular coaching consultations and adjustments',
  ]

  const eliteIncludes = [
    'Post-prep nutrition or reverse-diet guidance, when appropriate',
    'Personalized offseason nutrition',
    'Muscle-development training',
    'Recovery planning and management',
    'Physique assessments and progress reviews',
    'Individualized development toward your next physique goal',
    'Regular coaching consultations and adjustments',
  ]
  const strongAgain = coachingPricing.strongAgain[0]
  const nutrition = coachingPricing.nutritionBlueprint[0]
  const champions = coachingPricing.championsBlueprint
  const proLook = coachingPricing.proLook
  const elite = coachingPricing.eliteCompetitiveEdge

  const ConsultationButton = ({
    label = 'Schedule Your Complimentary Consultation',
    inverted = false,
  }: {
    label?: string
    inverted?: boolean
  }) => (
    <Link
      to="/book-consultation"
      className={
        inverted
          ? 'inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg'
          : 'inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg'
      }
    >
      {label}
      <ArrowRight className="ml-2" size={18} />
    </Link>
  )

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title="Coaching & Pricing — Fit by Cassie"
        description="Explore Fit by Cassie private strength, nutrition, physique, and competition coaching programs and founding-client investments."
      />

      {/* HERO */}
      <section className="px-4 sm:px-6 lg:px-8 pt-20 pb-16 sm:pt-24 sm:pb-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-blush mb-5">
              Coaching & Investment
            </span>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-navy leading-tight mb-6">
              Choose the support that fits
              <em className="block italic font-normal text-brand-teal">
                where you are now.
              </em>
            </h1>

            <p className="text-lg sm:text-xl text-brand-navy/65 max-w-3xl mx-auto leading-relaxed">
              No one-size-fits-all coaching. Whether you're building strength
              for the first time, finding your way back to consistency,
              getting your nutrition together, or preparing for a physique
              goal, your coaching should fit your starting point, your goals,
              and the life you're living right now.
            </p>
          </div>
        </div>
      </section>

      {/* STRONG AGAIN — FLAGSHIP */}
      <section className="px-4 sm:px-6 lg:px-8 pb-14">
        <div className="container-max">
          <div className="max-w-6xl mx-auto bg-brand-ivory rounded-3xl border border-brand-bronze/25 shadow-soft-lg overflow-hidden">
            <div className="grid lg:grid-cols-2">
              <div className="p-7 sm:p-10 lg:p-12">
                <span className="inline-block rounded-full bg-brand-blush/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-navy mb-6">
                  Signature Private Coaching
                </span>

                <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">
                  The Strong Again Method™
                </h2>

                <p className="font-semibold text-brand-teal mb-5">
                  Individualized 1:1 Strength, Nutrition & Accountability Coaching
                </p>

                <p className="text-brand-navy/65 leading-relaxed mb-7">
                  Built for women who want to get stronger, change their body,
                  and create consistency and confidence that fit real life.
                </p>

                <div className="space-y-4">
                  {strongAgainIncludes.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle
                        size={18}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span className="text-brand-navy/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-brand-navy p-7 sm:p-10 lg:p-12 flex flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-cream/65 mb-3">
                  Founding Client Investment
                </p>

                <p className="font-display text-5xl sm:text-6xl font-semibold text-brand-cream mb-2">
                  {strongAgain.price}
                </p>

                <p className="text-brand-cream/70 mb-1">
                  3-month minimum commitment
                </p>

                <p className="text-brand-cream/55 mb-8">
                  {strongAgain.detail}
                </p>

                <p className="text-brand-cream/70 leading-relaxed mb-8">
                  Start with a complimentary consultation so we can talk through
                  your goals, your history, and whether private coaching is the
                  right fit.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/strong-again/enroll"
                    className="inline-flex items-center justify-center rounded-full bg-brand-cream text-brand-navy font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                  >
                    Enroll Now
                    <ArrowRight className="ml-2" size={18} />
                  </Link>

                  <Link
                    to="/book-consultation"
                    className="inline-flex items-center justify-center rounded-full border-2 border-brand-cream text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:bg-brand-cream hover:text-brand-navy"
                  >
                    Schedule a Consultation First
                  </Link>
                </div>

                <p className="text-xs text-brand-cream/50 mt-4">
                  Ready to start? Enroll any time — a consultation is available
                  but never required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NUTRITION */}
      <section className="px-4 sm:px-6 lg:px-8 py-10">
        <div className="container-max">
          <div className="max-w-6xl mx-auto bg-brand-ivory rounded-3xl border border-brand-bronze/20 p-7 sm:p-10 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Nutrition Coaching
                </span>

                <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-3 mb-4">
                  Nutrition Blueprint
                </h2>

                <p className="text-brand-navy/65 leading-relaxed mb-7">
                  Personalized nutrition coaching for women who want more
                  clarity, structure, and consistency with food—without a
                  rigid plan that takes over their life.
                </p>

                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">
                  Founding Client Investment
                </p>

                <p className="font-display text-4xl font-semibold">
                  {nutrition.price}
                </p>

                <p className="text-brand-navy/55 mt-1 mb-8">
                  {nutrition.detail}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/enroll/nutritionBlueprint"
                    className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
                  >
                    Enroll Now
                    <ArrowRight className="ml-2" size={18} />
                  </Link>
                  <ConsultationButton label="Questions First? Schedule a Consultation" />
                </div>
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-5">
                  What's Included
                </p>

                <div className="space-y-4">
                  {nutritionIncludes.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle
                        size={18}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span className="text-brand-navy/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPETITION / PHYSIQUE */}
      <section className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="container-max">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-teal">
                Physique & Competition Coaching
              </span>

              <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-4 mb-4">
                Choose the path that matches your goal.
              </h2>

              <p className="text-brand-navy/65 leading-relaxed">
                From stage preparation to advanced physique development and
                offseason growth, your coaching path should match the outcome
                you're working toward.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-7">

              {/* CHAMPION'S BLUEPRINT */}
              <div className="bg-brand-ivory rounded-3xl border border-brand-bronze/20 p-7 sm:p-8 flex flex-col">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Competition Prep
                </span>

                <h3 className="font-display text-2xl font-semibold mt-3 mb-3">
                  Champion's Blueprint™
                </h3>

                <p className="text-brand-navy/60 text-sm leading-relaxed mb-6">
                  Structured contest-prep coaching designed to take you from
                  preparation through peak week and the stage.
                </p>

                <div className="space-y-3 border-y border-brand-bronze/20 py-6 mb-6">
                  {champions.map((option) => (
                    <div key={option.label} className="flex items-center justify-between gap-4">
                      <div>
                        <span className="block text-brand-navy/65">{option.label}</span>
                        <strong>{option.price}</strong>
                      </div>
                      <EnrollLink packageKey={tierKey('championsBlueprint', option.length?.value)} />
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-8">
                  {competitionIncludes.map((item) => (
                    <div key={item} className="flex items-start gap-2.5">
                      <CheckCircle
                        size={16}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-brand-navy/65">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <ConsultationButton label="Questions First? Schedule a Consultation" />
                </div>
              </div>

              {/* THE PRO LOOK */}
              <div className="bg-brand-ivory rounded-3xl border border-brand-teal/30 shadow-soft-lg p-7 sm:p-8 flex flex-col">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Advanced Physique / Event Goal
                </span>

                <h3 className="font-display text-2xl font-semibold mt-3 mb-3">
                  The Pro Look™
                </h3>

                <p className="text-brand-navy/60 text-sm leading-relaxed mb-6">
                  Advanced physique coaching for women working toward a
                  defined aesthetic goal, photoshoot, event, or higher level
                  of physique development.
                </p>

                <div className="space-y-3 border-y border-brand-bronze/20 py-6 mb-6">
                  {proLook.map((option) => (
                    <div key={option.label} className="flex items-center justify-between gap-4">
                      <div>
                        <span className="block text-brand-navy/65">{option.label}</span>
                        <strong>{option.price}</strong>
                      </div>
                      <EnrollLink packageKey={tierKey('proLook', option.length?.value)} />
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-8">
                  {proLookIncludes.map((item) => (
                    <div key={item} className="flex items-start gap-2.5">
                      <CheckCircle
                        size={16}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-brand-navy/65">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <ConsultationButton label="Questions First? Schedule a Consultation" />
                </div>
              </div>

              {/* ELITE COMPETITIVE EDGE */}
              <div className="bg-brand-ivory rounded-3xl border border-brand-bronze/20 p-7 sm:p-8 flex flex-col">
                <span className="text-xs font-bold uppercase tracking-widest text-brand-teal">
                  Offseason Development
                </span>

                <h3 className="font-display text-2xl font-semibold mt-3 mb-3">
                  Elite Competitive Edge™
                </h3>

                <p className="text-brand-navy/60 text-sm leading-relaxed mb-6">
                  Purposeful offseason development for competitors who need time
                  to build, refine, and prepare before the next prep begins.
                </p>

                <div className="space-y-3 border-y border-brand-bronze/20 py-6 mb-6">
                  {elite.map((option) => (
                    <div key={option.label} className="flex items-center justify-between gap-4">
                      <div>
                        <span className="block text-brand-navy/65">{option.label}</span>
                        <strong>{option.price}</strong>
                      </div>
                      <EnrollLink packageKey={tierKey('eliteCompetitiveEdge', option.length?.value)} />
                    </div>
                  ))}
                </div>

                <div className="space-y-3 mb-8">
                  {eliteIncludes.map((item) => (
                    <div key={item} className="flex items-start gap-2.5">
                      <CheckCircle
                        size={16}
                        className="text-brand-teal shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-brand-navy/65">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-auto">
                  <ConsultationButton label="Questions First? Schedule a Consultation" />
                </div>
              </div>

            </div>
            

          
          </div>
        </div>
      </section>

      {/* DMV ADD-ON */}
      <section className="px-4 sm:px-6 lg:px-8 py-10">
        <div className="container-max">
          <div className="max-w-5xl mx-auto rounded-3xl bg-brand-blush/15 border border-brand-bronze/20 p-7 sm:p-10">
            <span className="text-xs font-bold uppercase tracking-widest text-brand-teal">
              Optional In-Person Support
            </span>

            <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-3 mb-4">
              Online Coaching. Select In-Person Options in the DMV.
            </h2>

            <p className="text-brand-navy/65 leading-relaxed mb-3">
              Fit by Cassie is an online-first coaching business. You can
              follow your individualized plan at home, at your own gym, or
              wherever you prefer to train.
            </p>

            <p className="text-brand-navy/65 leading-relaxed">
              For clients in the DC, Maryland, and Virginia area, select
              in-person sessions may be available by arrangement for an
              additional fee, depending on location and scheduling. Ask about
              availability during your complimentary consultation.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-brand-navy px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-xs font-bold uppercase tracking-widest-xl text-brand-blush">
              Not Sure Which Path Fits?
            </span>

            <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-4 mb-5 text-brand-cream">
              Start with a conversation.
            </h2>

            <p className="text-brand-cream/70 max-w-2xl mx-auto leading-relaxed mb-2">
              You don't need to know exactly which program you need before we talk.
            </p>

            <p className="text-brand-cream/70 max-w-2xl mx-auto leading-relaxed mb-8">
              Tell me about your goals, your experience, and what your life
              looks like right now. We'll talk through your options and see
              which coaching path makes sense for you.
            </p>

            <ConsultationButton inverted />

            <p className="text-sm text-brand-cream/50 mt-5">
              Your starting point is information. It isn't a qualification test.
            </p>
          </div>
        </div>
      </section>
    </main>
  )
}

export default CoachingPricing
